import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';

import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { FormControlLabel } from '@material-ui/core';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';
import fr from 'facet-rest';
import { hasRelationship } from 'Helpers/Affordances';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import { PrimaryActionButton } from '@facet-wealth/design';
import ErrorList from 'Components/UI/ErrorList';

const styles = theme => ({
  root: {
    padding: 0,
  },
  hasError: {
    background: theme.palette.facet.brightRed12p,
  },
  popoverContainer: {
    padding: theme.spacing(2),
    width: theme.dimensions.md,
  },
  cellValueField: {
    padding: 0,
  },
  customRangeContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`,
  },
  labelText: {
    marginBottom: theme.spacing(1),
  },
  rangeTooltipHelper: {
    backgroundColor: theme.palette.primary.main,
  },
  enabledControl: {
    justifyContent: 'flex-end',
  },
  switchField: {
    marginBottom: 0,
  },
  rootHidden: {
    display: 'none',
  },
  clearButton: {
    marginBottom: theme.spacing(2),
  },
  when: {
    marginBottom: 0,
  },
  errorList: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
});

const TableCellLayout = ({
  classes,
  handleClose,
  attrName,
  onCellFormSubmitOverride,
  rowResource,
}) => {
  const {
    resource: cellResource, onSubmit, errors,
  } = useMetaLayoutContext();

  const { refreshPageContentCollection } = usePageContentContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const [submitting, setSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const customRangeSelected = cellResource.attr('when') === 'custom';

  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Modify Allocation Input Popover',
  });

  const handleOnSubmit = async () => {
    if (onCellFormSubmitOverride) {
      onCellFormSubmitOverride(rowResource, cellResource);
      logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'manual allocation submitted');
      handleClose();
      return;
    }
    try {
      setSubmitting(true);
      logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'manual allocation submitted');
      await onSubmit({ rejectOnError: true });
      displaySuccess('Successfully updated manual allocations');
      handleClose();
      refreshPageContentCollection();
      setSubmitting(false);
      setHasError(false);
    } catch (err) {
      displayErrorMessage(err, err.errors);
      setHasError(true);
      setSubmitting(false);
    }
  };

  const handleOnClickClearManualAllocationForRow = async () => {
    const autoAllocateUrl = cellResource.relationship(fr.relationships.autoAllocateOutcome.name).link('related').url();
    try {
      await fr.http.Post(autoAllocateUrl);
      displaySuccess('Successfully cleared manual allocations for row');
      handleClose();
      refreshPageContentCollection();
    } catch (e) {
      displayErrorMessage(e, 'Failed to clear manual allocations for row');
    }
  };

  return (
    <div className={classNames(classes.root, { [classes.hasError]: hasError })}>
      <div
        className={classes.popoverContainer}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
        role={'button'}
        tabIndex={0}
      >
        {hasRelationship(cellResource, fr.relationships.autoAllocateOutcome.name) && (
          <Button1
            test={'facet-plan-view-auto-allocate-button'}
            onClick={handleOnClickClearManualAllocationForRow}
            aria-label="Clear Manual Allocations For Row"
            className={classes.clearButton}
            color={'primary'}
            size={'small'}
            variant={'outlined'}
            fullWidth
            round
          >
            {'Clear Manual Allocations For Row'}
          </Button1>
        )}
        <Text value={'Create Manual Allocation'} align={'left'} variant={'h4'} className={classes.labelText} />
        <ErrorList errors={errors} className={classes.errorList} />
        <MetaField attr={attrName} />
        <FormControlLabel
          control={
            <MetaField className={classes.switchField} attr="shouldGrowWithInflation" />
              }
          classes={{ root: classes.enabledControl }}
          label="Grow With Inflation"
          labelPlacement="end"
        />
        <MetaField attr="when" className={classes.when} />
        {customRangeSelected && (
        <React.Fragment>
          <Text value={'Date Range'} align={'left'} variant={'h5'} className={classes.labelText} />
          <div className={classes.customRangeContainer}>
            <MetaField attr="overrideStartDate" />
            <MetaField attr="overrideEndDate" />
          </div>

          <Text value={'Frequency'} align={'left'} variant={'h5'} className={classes.labelText} />
          <MetaField attr="overrideRepeatFrequency" />
        </React.Fragment>
        )}
        <PrimaryActionButton
          onClick={() => handleOnSubmit()}
          loading={submitting}
          disabled={submitting}
          test={'facet-plan-allocation-table-cell-layout-save-button'}
          label={'Save'}
          isFullWidth
          size="lg"
        />
      </div>
    </div>
  );
};

TableCellLayout.propTypes = {
  classes: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  attrName: PropTypes.string.isRequired,
  onCellFormSubmitOverride: PropTypes.func,
  rowResource: PropTypes.object.isRequired,
};

TableCellLayout.defaultProps = {
  classes: {},
  onCellFormSubmitOverride: null,
};

export default withStyles(styles)(TableCellLayout);
